import UAParser from "ua-parser-js";
import Cookies from "js-cookie";
import { CmpInitResponse } from "@foundation/cmp-client";
import AdBlocker from "./ad_blocker/AdBlocker";
import AdDebugRegistry from "./debug/AdDebugRegistry";
import CompanionGroupCoordinator from "./refreshing/CompanionGroupCoordinator";
import GPT from "./gpt/GPT";
import TaglessAd from "./taglessAd/TaglessAd";
import DoubleVerifyTracking from "./doubleVerify/DoubleVerifyTracking";
import identity from "./util/identity";
import PostMessages from "./communication/PostMessages";
import Renderer from "./rendering/Renderer";
import UserInfo from "./session/UserInfo";
import Viewability from "./viewability/Viewability";
import Visibility from "./viewability/Visibility";
import getObjectSignature from "./util/getObjectSignature";
import PerformanceMonitor from "./util/monitoring/PerformanceMonitor";
import makeMeasure from "./util/makeMeasure";
import Ad from "./gpt/Ad";
import getUserConsentData from "./util/consent";
import Bombora from "./bombora/Bombora";
import fireEREvt from "./util/eventRecorderClient";
import { urlModifier } from "./util/targetingModifiers";
import AdsServiceClient from "./adsService/AdsServiceClient";
import { doubleVerifyScriptUrl } from "./doubleVerify/doubleVerifyScript";
import { checkIfTaglessAdEnabled } from "./util/pageEnabledChecker";
import { EventRecorderLoader } from "./eventrecorder/EventRecorderLoader";
import { AdContextData, AdOptions, Destructible, SimpleCb, SparkleInitOptions, SparkleStub, TargetingDecorator } from "./type";
import { Targeting } from "./interfaces/TargetingProvider";
import SlotRenderEndedEvent from "./gpt/SlotRenderEndedEvent";
import CompanionGroup from "./refreshing/CompanionGroup";
import { WatchlistProvider } from "./watchlists/WatchlistProvider";

const DISABLE_SPARKLE_COOKIE_NAME = "term";

const SET_IFRAME_HEIGHT_METHOD = "setIframeHeight";
const SET_IFRAME_DIMENSIONS_METHOD = "setIframeDimensions";

const SUBSCRIBER_TARGETING_KEY = "subscriber";

function composeFunctions(listOfFunctions: TargetingDecorator[], initialValue: Targeting) {
    return listOfFunctions.reduce(
        async (oldValue, newFunction) => newFunction(await oldValue),
        Promise.resolve(initialValue)
    );
}

const makeSetIframeHeightListenerFor = (ad: Ad) =>
    ({ height }: { height: string }) => ad.setHeight(height);
const makeSetIframeDimensionsListenerFor = (ad: Ad) =>
    ({ width, height }: { width: string, height: string }) => ad.setDimensions(width, height);

const FALL_BACK_AD_PAGE_IDS = ["industries"];
const CONSENT_TIMEOUT_PAGE_IDS = ["industries"];
const checkIfFallbackAdEnabled = (pageId: string) => FALL_BACK_AD_PAGE_IDS.includes(pageId);
const checkIfConsentTimeoutEnabled = (pageId: string) => CONSENT_TIMEOUT_PAGE_IDS.includes(pageId);

export default class Sparkle implements Destructible {

    adCodeDecoratorFunction: (adCode: string) => string = identity;
    userInfo = new UserInfo();
    adsServiceClient: AdsServiceClient;
    watchlistProvider: WatchlistProvider;
    gpt: GPT;
    gamTaglessAd: TaglessAd;
    postMessages: PostMessages;
    viewability: Viewability;
    visibility: Visibility;
    renderer: Renderer;
    bombora: Bombora;

    cmd: { push: (cb: SimpleCb) => void } = [] as SimpleCb[];

    initialized = false;
    initPending = false;

    isDecoupled = false;

    isFallbackAdEnabled = false;

    isConsentTimeoutEnabled = false;

    isTaglessAdEnabled = false;

    disabled = Cookies.get(DISABLE_SPARKLE_COOKIE_NAME) === "equality";

    /**
     * @type {string}
     */
    version = process.env.VERSION;

    /**
     * Queue for all methods invoked prior to init()
     * @type {Function[]}
     * @private
     */
    _initQueue: SimpleCb[] = [];
    /**
     * @type {*[]}
     * @private
     */
    _targets = [];

    /**
     * @type {*[]}
     * @private
     */
    _destructibles: Destructible[] = [];

    /**
     * @type {PerformanceMonitor}
     * @private
     */
    _performanceMonitor = new PerformanceMonitor("Sparkle");

    /**
     * @type {Function}
     * @private
     */
    _measure = makeMeasure(new PerformanceMonitor("Sparkle"));

    /**
     * @type {AdDebugRegistry}
     * @private
     */
    _adDebugRegistry = new AdDebugRegistry();

    /**
     * @type {CompanionGroupCoordinator}
     * @private
     */
    _companionGroupCoordinator = new CompanionGroupCoordinator();

    _adMap: Map<string, Ad> = new Map();

    GPT = {
        html: GPT.html
    };

    /**
     * @param {Object|Sparkle} [stub]
     * @return {Sparkle}
     */
    constructor(stub: SparkleStub = {}) {
        const stubSignature = stub && getObjectSignature(Object.getPrototypeOf(stub));
        const selfSignature = getObjectSignature(Object.getPrototypeOf(this));

        // Check if stub is already an instance of Sparkle
        if (stubSignature === selfSignature) {
            return stub as this;
        }

        this.cmd = stub && stub.cmd || this.cmd;
        this.cmd.push = (cb) => {
            if (typeof cb === "function") cb();
        };
    }

    showDebugInformationOnRender() {
        PerformanceMonitor.printInstructions();
        this._adDebugRegistry.showDebugInformationOnRender();

        return this;
    }

    _init({
        disableTrigr = false,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        useCachedConsent = false,
        networkCode,
        breakpoints,
        adCodeDecorator = identity,
        adBlockerShouldBeBlocked = true,
        pageTargeting = {},
        observerOptions,
        isDecoupled = false,
        dvScriptUrl = doubleVerifyScriptUrl,
        adContextData = {},
        application,
        enableEventRecorder = false,
        excludeTargetingTypes
    }: SparkleInitOptions) {
        new EventRecorderLoader({ application, enabled: enableEventRecorder }).init();

        fireEREvt("ad-init", { breakpoints, pageTargeting });

        this.isDecoupled = isDecoupled;

        const { page: pageId = "" } = pageTargeting;

        this.isFallbackAdEnabled = checkIfFallbackAdEnabled(pageId);
        this.isConsentTimeoutEnabled = checkIfConsentTimeoutEnabled(pageId);

        this.isTaglessAdEnabled = checkIfTaglessAdEnabled(pageId);

        if (this.disabled) {
            return Promise.resolve();
        }

        const ua = new UAParser();
        const { name, major } = ua.getBrowser();

        const doubleVerifyTracking = new DoubleVerifyTracking(dvScriptUrl);

        this.adCodeDecoratorFunction = adCodeDecorator;
        this.adsServiceClient = new AdsServiceClient(
            this.userInfo,
            pageId,
            isDecoupled,
            true, // useCachedTargeting
            adContextData,
            excludeTargetingTypes,
            disableTrigr
        );
        this.watchlistProvider = new WatchlistProvider(this.userInfo);
        // Trigger process of getting targeting kv from AdService
        // It will fetch the IVT related targetings.
        // If isDecoupled flag is enabled, we will get back the personalization
        // targetings too.
        this.adsServiceClient.getTargetingFromAdsService();
        this.gpt = new GPT(
            networkCode,
            doubleVerifyTracking,
            this.adsServiceClient,
            this.watchlistProvider,
            isDecoupled
        );

        // `subscriber` is page-level rather than slot-level targeting: a host
        // that sets it on its own slots still wins (GAM applies slot-level
        // over page-level), but nothing else can shadow it and leave it
        // stale for the life of the slot. Doesn't cover a host passing
        // `subscriber` via `pageTargeting`/`addPageTargeting` instead -
        // not observed in practice.
        //
        // Resolved once here for the whole page. If the reg-ui-client is not
        // up yet this falls back to the previous page load's value and the
        // listener below re-publishes once corrected.
        this.userInfo.onSubscriberChange(
            (subsInfo) => this.gpt.setTargeting(SUBSCRIBER_TARGETING_KEY, subsInfo)
        );
        this.userInfo.resolveSubscriberInfo();

        this.gamTaglessAd = new TaglessAd();
        this.postMessages = new PostMessages();
        this.viewability = new Viewability(breakpoints);
        this.visibility = new Visibility();
        this.renderer = new Renderer(
            this.gpt,
            this.gamTaglessAd,
            this.isTaglessAdEnabled,
            this.viewability,
            this.visibility,
            null,
            observerOptions
        );

        if (this.isDecoupled) {
            // enable debugAds
            //
            // debugAds is currently enable in foundation/sparkle, which
            // happens after decouple ad is created. Therefore we need to
            // enable debugAds here.
            // In the future, this kind of logic should probably be migrated
            // from foundation to bb.ads for all ads
            // @ts-expect-error ts-migration
            const urlObj = new URL(window.location);

            if (urlObj.searchParams.get("debugAds") === "true") {
                this.showDebugInformationOnRender();
            }

        }

        this.bombora = new Bombora(this.gpt);

        this.gpt.setTargeting("browser", `${ name }${ major }`);

        /**
         * Register objects that can be destroyed
         * @type {*[]}
         * @private
         */
        this._destructibles = [
            ...this._destructibles,
            this._adDebugRegistry,
            this._companionGroupCoordinator,
            this.gpt,
            this.postMessages,
            this.renderer,
            this.userInfo,
            this.viewability,
            this.visibility
        ];

        /**
         * Automatic clean-up before leaving the page
         */
        // Disabling this error to see if this fixes errors with Bazaar new Nav launch
        // window.addEventListener("beforeunload", () => this.destroy());

        return this._measure("waitForUserConsent", getUserConsentData, this.isConsentTimeoutEnabled)
            .then((consentData) => {
                try {
                    this.bombora.init();
                } catch (err) {
                    console.error(err);
                }

                try {
                    doubleVerifyTracking.init();
                } catch (err) {
                    console.error(err);
                }

                if (!consentData && this.isConsentTimeoutEnabled) {
                    fireEREvt("ad-consent-timeout");
                    this.renderer.setFallbackFlag(true);
                    Promise.resolve();
                } else {
                    this._measure("addPageTargeting", this.initPageTargeting, pageTargeting, consentData);
                }
            })
            .then(() => {
                if (adBlockerShouldBeBlocked) {
                    AdBlocker.blockTheAdBlocker();
                }

                this.initialized = true;
                this._flushInitQueue();
            })
            .catch((err) => console.error(err));
    }

    init(options: SparkleInitOptions): void {
        if (this.initPending || this.initialized) {
            return;
        }

        this.initPending = true;
        this._measure("initialize", this._init, options)
            .finally(() => {
                this.initPending = false;
            });
    }

    addPageTargeting(pageTargeting: Targeting) {
        return Promise.resolve(this._setGptTargeting(pageTargeting));
    }

    initPageTargeting(pageTargeting: Targeting, consentData: CmpInitResponse) {
        fireEREvt("ad-page-targeting");

        return this.gpt.setPersonalization(this.userInfo, consentData.gdprApplies)
            .then((uid) => {
                const { gdprApplies, usPrivacyString, iabConsentString } = consentData;
                const exp_pref = Cookies.get("exp_pref");
                const newTargeting = {
                    exp_pref,
                    uid,
                    gdpr: gdprApplies ? "1" : "0",
                    us_privacy: usPrivacyString,
                    gdpr_consent: iabConsentString,
                    ...pageTargeting
                };

                this.gamTaglessAd.addTargeting({
                    gdpr: newTargeting.gdpr,
                    us_privacy: newTargeting.us_privacy,
                    gdpr_consent: newTargeting.gdpr_consent
                });

                this._setGptTargeting(newTargeting);
                fireEREvt("ad-page-targeting-end");
            });
    }

    _setGptTargeting(targeting: Record<string, unknown>) {
        Object.keys(targeting)
            .filter((key) => targeting[key] !== undefined && targeting[key] !== null)
            .forEach((key) => this.gpt.setTargeting(key, targeting[key] as string));
    }

    async createAd(adOptions: AdOptions): Promise<Ad> {
        if (this.disabled) {
            return new Promise(() => {
            });
        }

        /**
         * Queue ad creation until Sparkle is initialized
         */
        if (!this.initialized) {
            return this._enqueue(() => this.createAd(adOptions));
        }

        const {
            containerId,
            adCode,
            dimensions,
            targeting,
            strategy,
            viewportOffset = 0,
            targetingModifiers = [],
            isInterstitial = false,
            isDecoupled = false,
            shadowHostElement
        } = adOptions;

        fireEREvt("ad-create", { "container-id": containerId, "ad-code": adCode });

        if (!(containerId && adCode && dimensions)) {
            const msg = `Sparkle#createAd requires containerId, adCode and dimensions ${ containerId }, ${ adCode }, ${ dimensions }`;

            fireEREvt("ad-err", msg);
            throw new Error(msg);
        }

        /**
         * Performance ID is used for tagging performance events
         * @type {string}
         */
        const performanceId = `${ containerId }@${ targeting.position }`;

        const createAdMetric = this._performanceMonitor.createMetric(`createAd-${ performanceId }`);

        // include targeting modifiers time as part of the createAd metric
        createAdMetric.start();

        const newTargeting = await composeFunctions(
            [
                ...targetingModifiers,
                ...(isDecoupled ? [urlModifier] : [])
            ],
            targeting
        );

        newTargeting.performanceId = performanceId;

        // `subscriber` is deliberately not in the ad's targeting - it is set
        // page-level in _init. Report what the request will actually carry:
        // a host-supplied slot-level value overrides page-level in GAM.
        const subscriber = (newTargeting.subscriber as string | undefined) ??
            this.userInfo.getSubscriberInfo();

        const adRenderMetric = this._performanceMonitor.createMetric(`renderAd-${ newTargeting.performanceId }`);
        const adLoadMetric = this._performanceMonitor.createMetric(`loadAd-${ newTargeting.performanceId }`);

        const ad = this.gpt.createAd(
            this.viewability,
            containerId,
            this.adCodeDecoratorFunction(adCode),
            dimensions,
            newTargeting,
            isDecoupled,
            shadowHostElement
        )
            .setViewportOffset(viewportOffset)
            .setInterstitialFlag(isInterstitial);

        this._adDebugRegistry.push(ad, containerId, this.gpt);
        // for now, only store decouple ad slot
        if (isDecoupled) {
            this._adMap.set(containerId, ad);
        }
        // dispatch create ad event
        ad.notifyAdCreated();

        this.postMessages.addEventListener(
            ad.getId(),
            SET_IFRAME_HEIGHT_METHOD,
            makeSetIframeHeightListenerFor(ad)
        );
        this.postMessages.addEventListener(
            ad.getId(),
            SET_IFRAME_DIMENSIONS_METHOD,
            makeSetIframeDimensionsListenerFor(ad)
        );

        ad.addEventListener(Ad.EVENTS.ON_BEFORE_RENDER, () => {
            adRenderMetric.start();
        }, { once: true });
        ad.addEventListener<SlotRenderEndedEvent>(Ad.EVENTS.ON_RENDER,
            (event) => {
                adRenderMetric.end(event.toJSON());
                adLoadMetric.start();
            }, { once: true });
        ad.addEventListener<SlotRenderEndedEvent>(Ad.EVENTS.ON_EMPTY,
            (event) => {
                adRenderMetric.end(event.toJSON());
                adLoadMetric.start();

                if (this.isFallbackAdEnabled) {
                    this.renderer.setFallbackFlag(true);
                    this.renderer.render(ad, { strategy });
                }

            }, { once: true });
        ad.addEventListener(Ad.EVENTS.ON_LOAD, () => adLoadMetric.end(), { once: true });
        this.renderer.render(ad, { strategy });

        createAdMetric.end();
        fireEREvt("ad-create-end", { "container-id": containerId, subscriber: subscriber || null });

        this.adsServiceClient.setAd(ad);

        return ad;
    }

    getAd(containerId: string): Ad | undefined {
        return this._adMap.get(containerId);
    }

    // deprecated
    createInterstitialAd(adOptions: AdOptions): Promise<Ad> {
        Object.defineProperties(adOptions, {
            isInterstitial: { value: true },
            strategy: { value: "always" },
            dimensions: { value: [0] }
        });

        return this.createAd(adOptions);
    }

    disableCompanionAdBackfilling(): this {
        if (!this.initialized) {
            this._enqueue(() => this.gpt.setRefreshUnfilledSlots(false));

            return this;
        }

        this.gpt.setRefreshUnfilledSlots(false);

        return this;
    }

    enableVideoAds(): this {
        if (!this.initialized) {
            this._enqueue(() => this.gpt.enableVideoAds());

            return this;
        }

        this.gpt.enableVideoAds();

        return this;
    }

    getCompanionGroup(companionId: string): CompanionGroup {
        return this._companionGroupCoordinator.findOrCreate(companionId);
    }

    clearAllCompanionChildren(): void {
        this._companionGroupCoordinator.clearAllChildren();
    }

    refresh(ads: Ad[], options = {}) {
        const refreshAds = () => {
            if (!this.renderer) return;

            ads.forEach((ad: Ad) => {
                const name = `refreshAd-${ ad.getTargeting().performanceId }.${ ad.getAndIncrementRefreshAttempt() }`;
                const adRefreshMetric = this._performanceMonitor.createMetric(name);

                ad.addEventListener(Ad.EVENTS.ON_BEFORE_RENDER, () => {
                    adRefreshMetric.start();
                }, { once: true });
                ad.addEventListener<SlotRenderEndedEvent>(Ad.EVENTS.ON_RENDER,
                    (event) => {
                        adRefreshMetric.end(event.toJSON());
                    }, { once: true });
                ad.addEventListener<SlotRenderEndedEvent>(Ad.EVENTS.ON_EMPTY,
                    (event) => {
                        adRefreshMetric.end(event.toJSON());
                    }, { once: true });
            });

            this.renderer.refresh(ads, options);
        };

        if (!this.initialized) {
            this._enqueue(refreshAds);

            return this;
        }

        refreshAds();

        return this;
    }

    destroy() {
        // @ts-expect-error ts-migration
        this._adDebugRegistry = null;
        // @ts-expect-error ts-migration
        this._companionGroupCoordinator = null;
        // @ts-expect-error ts-migration
        this._targets = null;
        // @ts-expect-error ts-migration
        this.adCodeDecoratorFunction = null;
        // @ts-expect-error ts-migration
        this.gpt = null;
        // @ts-expect-error ts-migration
        this.postMessages = null;
        // @ts-expect-error ts-migration
        this.renderer = null;
        // @ts-expect-error ts-migration
        this.viewability = null;
        // @ts-expect-error ts-migration
        this.visibility = null;

        PerformanceMonitor.metrics.clear();

        let destructible = this._destructibles.shift();

        while (destructible) {
            if (typeof destructible.destroy === "function") {
                destructible.destroy();
            }
            destructible = this._destructibles.shift();
        }
    }

    showDebugInformation() {
        this._adDebugRegistry.showDebugInformation(this.gpt);

        return this;
    }

    updateCorrelator() {
        if (!this.initialized) {
            this._enqueue(() => this.gpt.updateCorrelator());

            return this;
        }

        this.gpt.updateCorrelator();

        return this;
    }

    resetAdContext(newAdContextData: AdContextData) {
        // The AdServiceClient will update the ad context data
        // and then fetch targetings again from AdService
        this.adsServiceClient.resetTargetings(newAdContextData);

        return this;
    }

    /**
     * Queues execution until initialized
     * @param cb
     * @returns {Promise<*>}
     * @private
     */
    _enqueue<T>(cb: () => T): Promise<T> {
        return new Promise((resolve) => {
            this._initQueue.push(() => resolve(cb()));
        });
    }

    private _flushInitQueue() {
        // @ts-expect-error ts-migration
        this._initQueue.push = (cb) => {
            if (typeof cb === "function") cb();
        };

        let cb = this._initQueue.shift();

        while (cb) {
            cb();
            cb = this._initQueue.shift();
        }
    }

    _flushQueue() {
        if (Array.isArray(this.cmd)) {
            let cb = this.cmd.shift();

            while (cb) {
                if (typeof cb === "function") cb();
                cb = this.cmd.shift();
            }
        }
    }

    /**
     * Used inside Dev Tools console to generate a table of performance measurements
     */
    logPerformanceInfo(): void {
        PerformanceMonitor.logPerformanceInfo();
    }

    /**
     * Used inside Dev Tools console to generate a table of performance measurements for an Ad
     */
    logIndividualAdPerformance(position: string): void {
        PerformanceMonitor.logIndividualAdPerformance(position);
    }

}
